<template>
  <div class="card-designs-index-container">
    <section class="card-designs__section">
      <PageHeadlineWithLink
        class="card-designs__head"
        title="カードデザイン一覧"
        link-title="戻る"
        @click-link="onClickBack"
      />
      <div>
        <div class="feedback">
          <transition name="fade" @enter="enterErrorFeedback">
            <feedback-error
              v-if="showableErrorFeedback"
              :message="errorMessage"
            />
          </transition>
        </div>
        <div
          v-resize="setStickyStyleIfNeeded"
          class="card-designs__categories-wrapper"
          :style="stickyStyle"
        >
          <div class="card-designs__categories">
            <div v-if="isLoadingCategories" class="card-designs__loading">
              <div class="loading">
                <div v-for="index in 12" :key="index" />
              </div>
            </div>
            <div v-else>
              <ul class="item-boxes--scrollable">
                <li
                  class="item-box"
                  :class="{ selected_category: isSelectedCategory('0') }"
                  @click="onClickCategory('0')"
                >
                  <p class="item-box__label">すべて</p>
                </li>
                <li
                  v-for="category in categories"
                  :key="category.id"
                  class="item-box"
                  :class="{
                    selected_category: isSelectedCategory(category.id)
                  }"
                  @click="onClickCategory(category.id)"
                >
                  <p class="item-box__label">
                    {{ category.name }}
                  </p>
                </li>
              </ul>
            </div>
          </div>
          <div class="card-designs__categories--pulldown">
            <p class="body card-categories-pulldown__label">
              カテゴリで絞り込む
            </p>
            <SortWithBlackChevronArea
              :options="categoriesForSort"
              :selected-value="selectedCategoryId"
              @change-sort="onClickCategory"
            />
          </div>
        </div>
        <div class="card-designs__items">
          <div v-if="isLoadingDesigns" class="card-designs__loading">
            <div class="loading loading--lg">
              <div v-for="index in 12" :key="index" />
            </div>
          </div>
          <div v-else>
            <div class="card-designs__item-boxes-wrapper">
              <!-- NOTE: preLoad = 読み込みを始める高さ (単位は比率っぽいけどよくわからず) -->
              <!-- TODO: loadingとerrorの画像は仮なので差し替える -->
              <div class="card-designs__item-boxes">
                <div
                  v-for="design in filteredCategories"
                  :key="design.id"
                  class="card-designs__item-box"
                  @click="onClickDesign(design)"
                >
                  <ImgLazy
                    :image-url="design.image_box_url"
                    :alt-text="design.name"
                    aspect-ratio="1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PageHeadlineWithLink from '../../molecules/headlines/PageHeadlineWithLink.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import axios from 'axios'
import SortWithBlackChevronArea from '../../atoms/sorts/SortWithBlackChevronArea.vue'
import ImgLazy from '../../atoms/images/ImgLazy.vue'

export default {
  components: {
    SortWithBlackChevronArea,
    FeedbackError,
    PageHeadlineWithLink,
    ImgLazy
  },
  data() {
    return {
      selectedCategoryId: '0',
      stickyStyle: {},
      categories: [],
      categoriesForSort: [],
      designs: [],
      isLoadingDesigns: false,
      isLoadingCategories: false,
      isUpdatingCardDesign: false,
      showableErrorFeedback: false,
      errorMessage: '',
      needToScroll: false
    }
  },
  computed: {
    filteredCategories() {
      if (this.selectedCategoryId === '0') {
        return this.designs
      }
      return this.designs.filter(design =>
        design.category_ids.includes(String(this.selectedCategoryId))
      )
    }
  },
  mounted() {
    this.setStickyStyleIfNeeded()
    this.getCardDesignCategories()
    this.getCardDesigns()
  },
  updated() {
    // モバイル表示でクエリパラメータから絞り込みを行った際に選択したカテゴリが表示されるようカテゴリの表示エリアをスクロールする。
    // カテゴリの取得をmountedで行っており、それ以降のライフサイクルでなければdomが存在しないため、updateで実行する。
    // updateはデータが更新されるたびに実行されるので、ユーザーの操作時にはスクロールされないよう、needToScrollで管理を行う。
    if (this.needToScroll) {
      this.scrollToSelectedCategory()
    }
  },
  methods: {
    onClickBack() {
      window.history.back()
    },
    onClickCategory(clicked_id) {
      dataLayer.push({
        event: 'select_content',
        content_type: 'card_design_category',
        item_id: clicked_id
      })
      this.selectedCategoryId = clicked_id
    },
    isSelectedCategory(id) {
      return this.selectedCategoryId === id
    },
    setStickyStyleIfNeeded() {
      const headers = document.querySelectorAll('header.header')
      if (headers.length !== 1) {
        return {}
      }
      const headerBottomRect = headers[0].getBoundingClientRect().bottom
      this.stickyStyle = {
        position: 'sticky',
        top: `${headerBottomRect}px`
      }
    },
    async onClickDesign(design) {
      // リクエスト中に別のカードがクリックされても無視し、先にクリックされたカードの処理を優先する
      if (this.isUpdatingCardDesign) {
        return
      }
      this.isUpdatingCardDesign = true

      try {
        await axios.patch('/api/v1/pickup/gift_card_sessions', {
          gift_card_session: {
            card_design_id: design.id
          }
        })
        window.location.href = '/gift_card/edit'
      } catch (error) {
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isUpdatingCardDesign = false
      }
    },
    async getCardDesignCategories() {
      this.isLoadingCategories = true

      try {
        const res = await axios.get('/api/v1/pickup/card_design_categories')
        this.categories = res.data.data.map(card_design_categories => {
          return {
            id: card_design_categories.attributes.id.toString(),
            name: card_design_categories.attributes.name
          }
        })
        this.categoriesForSort = this.categories.map(v => ({
          key: v.name,
          value: v.id.toString()
        }))
        this.categoriesForSort = [{ key: 'すべて', value: '0' }].concat(
          this.categoriesForSort
        )
        // クエリパラメータで存在しないカテゴリIDが渡された場合の対応のため、カテゴリIDの取得が完了したあとにクエリパラメータからの絞り込みを行う
        this.selectQueryCategoryId()
        this.isLoadingCategories = false
      } catch (error) {
        this.isLoadingCategories = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getCardDesigns() {
      this.isLoadingDesigns = true

      try {
        const res = await axios.get('/api/v1/pickup/card_designs')
        this.designs = res.data.data.map(card_designs => {
          return {
            id: card_designs.attributes.id,
            name: card_designs.attributes.name,
            image_url: card_designs.attributes.image_url,
            image_box_url: card_designs.attributes.image_box_url,
            category_ids:
              card_designs.relationships.card_design_categories.data.map(
                (_, index) => {
                  return card_designs.relationships.card_design_categories.data[
                    index
                  ].id
                }
              )
          }
        })
        this.isLoadingDesigns = false
      } catch (error) {
        this.isLoadingDesigns = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    scrollToSelectedCategory() {
      document.querySelector('.item-boxes--scrollable').scrollTo(
        // 左側に項目が存在するカテゴリの場合に左側のカテゴリを少し表示するため、offsetの位置からずらしている。
        document.querySelector('.selected_category').offsetLeft - 100,
        0
      )
      this.needToScroll = false
    },
    selectQueryCategoryId() {
      const regexpMatchedQueryCategoryIds =
        window.location.href.match(/category_id=([0-9]+)/)
      if (!regexpMatchedQueryCategoryIds) {
        return
      }

      const queryCategoryId = regexpMatchedQueryCategoryIds[1]
      const isExistsCategory = this.categories.some(category => {
        return category.id === queryCategoryId
      })

      // 存在しないカテゴリIDが指定されている場合、「すべて」を表示する。
      if (!isExistsCategory) {
        return
      }

      this.onClickCategory(queryCategoryId)
      this.needToScroll = true
    }
  }
}
</script>
